/**
 * Image Sharing Utility Functions
 * Handles sharing images directly to WhatsApp and other platforms
 */

/**
 * Downloads an image and shares it directly to WhatsApp mobile
 * This works best on mobile devices where WhatsApp is installed
 * 
 * @param {string} imageUrl - URL of the image to share
 * @param {string} text - Optional text to include with the image
 */
export const shareImageToWhatsApp = async (imageUrl, text = '') => {
  try {
    // Check if the Web Share API is available
    if (navigator.share && navigator.canShare) {
      // Fetch the image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create a file from the blob
      const file = new File([blob], 'product-image.jpg', { type: blob.type });
      
      // Create share data
      const shareData = {
        files: [file],
        text: text || 'Check out this product!'
      };
      
      // Check if we can share this data
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return true;
      }
    }
    
    // Fallback for desktop or if Web Share API is not available
    // Open WhatsApp with the image URL
    const phoneNumber = "919205894678"; // Format: country code + number without +
    const encodedText = encodeURIComponent(text || 'Check out this product!');
    const encodedImageUrl = encodeURIComponent(imageUrl);
    
    // On desktop, we can only share the URL, not the actual image
    window.open(`https://wa.me/${phoneNumber}?text=${encodedText}%20${encodedImageUrl}`, '_blank');
    return true;
  } catch (error) {
    console.error('Error sharing image:', error);
    return false;
  }
};

/**
 * Creates a downloadable link for an image
 * This can be used as a fallback when direct sharing is not available
 * 
 * @param {string} imageUrl - URL of the image to download
 * @param {string} fileName - Name to give the downloaded file
 */
export const downloadImage = async (imageUrl, fileName = 'product-image.jpg') => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    // Create a download link
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(downloadUrl);
    return true;
  } catch (error) {
    console.error('Error downloading image:', error);
    return false;
  }
};

export default {
  shareImageToWhatsApp,
  downloadImage
}; 